import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { 
  ArrowLeft,
  Search,
  Tv,
  Plus,
  Edit,
  Trash2,
  ExternalLink,
  Image,
  Play,
  Eye,
  EyeOff
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminChannels = () => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    stream_url: '',
    logo_url: '',
    banner_url: '',
    category: '',
    is_active: true
  });

  const categories = [
    'Nacional', 'Internacional', 'Notícias', 'Esportes', 'Entretenimento',
    'Documentários', 'Infantil', 'Filmes', 'Séries', 'Música', 'Religioso'
  ];

  useEffect(() => {
    fetchChannels();
  }, []);

  const fetchChannels = async () => {
    try {
      const response = await axios.get(`${API}/admin/channels`);
      setChannels(response.data);
    } catch (error) {
      console.error('Error fetching channels:', error);
      toast.error('Erro ao carregar canais');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingChannel) {
        await axios.put(`${API}/admin/channels/${editingChannel.id}`, formData);
        toast.success('Canal atualizado com sucesso!');
      } else {
        await axios.post(`${API}/admin/channels`, formData);
        toast.success('Canal criado com sucesso!');
      }
      
      setShowModal(false);
      setEditingChannel(null);
      resetForm();
      fetchChannels();
    } catch (error) {
      console.error('Error saving channel:', error);
      toast.error('Erro ao salvar canal');
    }
  };

  const handleEdit = (channel) => {
    setEditingChannel(channel);
    setFormData({
      name: channel.name,
      description: channel.description || '',
      stream_url: channel.stream_url,
      logo_url: channel.logo_url || '',
      banner_url: channel.banner_url || '',
      category: channel.category || '',
      is_active: channel.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (channelId) => {
    if (window.confirm('Tem certeza que deseja excluir este canal?')) {
      try {
        await axios.delete(`${API}/admin/channels/${channelId}`);
        toast.success('Canal excluído com sucesso!');
        fetchChannels();
      } catch (error) {
        console.error('Error deleting channel:', error);
        toast.error('Erro ao excluir canal');
      }
    }
  };

  const toggleChannelStatus = async (channelId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/channels/${channelId}`, {
        is_active: !currentStatus
      });
      toast.success(`Canal ${!currentStatus ? 'ativado' : 'desativado'} com sucesso!`);
      fetchChannels();
    } catch (error) {
      console.error('Error updating channel status:', error);
      toast.error('Erro ao atualizar status do canal');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      stream_url: '',
      logo_url: '',
      banner_url: '',
      category: '',
      is_active: true
    });
  };

  const handleNewChannel = () => {
    setEditingChannel(null);
    resetForm();
    setShowModal(true);
  };

  const filteredChannels = channels.filter(channel => {
    const matchesSearch = channel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (channel.description && channel.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = categoryFilter === 'all' || channel.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4" />
          <p className="text-white">Carregando canais...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin">
              <Button variant="outline" size="sm" className="text-white border-white/20">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-white flex items-center">
              <Tv className="mr-3 h-6 w-6" />
              Gerenciar Canais
            </h1>
          </div>
          
          <Button onClick={handleNewChannel} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="h-4 w-4 mr-2" />
            Novo Canal
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar canais..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-white/5 border-white/20 text-white"
                  />
                </div>
              </div>
              
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-48 bg-white/5 border-white/20 text-white">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Categorias</SelectItem>
                  {categories.map(category => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Channels Grid */}
        {filteredChannels.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-12 text-center">
              <Tv className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {searchTerm || categoryFilter !== 'all' ? 'Nenhum canal encontrado' : 'Nenhum canal cadastrado'}
              </h3>
              <p className="text-gray-400 mb-6">
                {searchTerm || categoryFilter !== 'all' 
                  ? 'Tente ajustar os filtros de busca' 
                  : 'Comece criando seu primeiro canal'
                }
              </p>
              {!searchTerm && categoryFilter === 'all' && (
                <Button onClick={handleNewChannel} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Primeiro Canal
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredChannels.map((channel) => (
              <Card key={channel.id} className="bg-white/5 border-white/10 hover:bg-white/10 transition-all">
                <CardContent className="p-0">
                  {/* Channel Preview */}
                  <div className="aspect-video bg-gradient-to-br from-blue-500 to-purple-600 rounded-t-lg flex items-center justify-center relative overflow-hidden">
                    {channel.banner_url || channel.logo_url ? (
                      <img 
                        src={channel.banner_url || channel.logo_url} 
                        alt={channel.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="w-full h-full flex items-center justify-center">
                      <Tv className="h-12 w-12 text-white" />
                    </div>
                    
                    {/* Status Badge */}
                    <div className="absolute top-3 right-3">
                      <Badge 
                        variant="secondary" 
                        className={channel.is_active 
                          ? "bg-green-500/20 text-green-300" 
                          : "bg-red-500/20 text-red-300"
                        }
                      >
                        {channel.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Channel Info */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold text-white text-lg">{channel.name}</h3>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleChannelStatus(channel.id, channel.is_active)}
                        className="text-gray-400 hover:text-white p-1"
                      >
                        {channel.is_active ? (
                          <Eye className="h-4 w-4" />
                        ) : (
                          <EyeOff className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    
                    {channel.description && (
                      <p className="text-gray-400 text-sm mb-3 line-clamp-2">
                        {channel.description}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between mb-4">
                      {channel.category && (
                        <Badge variant="secondary" className="bg-blue-500/20 text-blue-300">
                          {channel.category}
                        </Badge>
                      )}
                    </div>

                    {/* Stream URL */}
                    <div className="mb-4">
                      <p className="text-xs text-gray-500 mb-1">URL do Stream</p>
                      <div className="flex items-center space-x-2">
                        <p className="text-xs text-gray-300 truncate flex-1 font-mono bg-black/20 px-2 py-1 rounded">
                          {channel.stream_url}
                        </p>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.open(channel.stream_url, '_blank')}
                          className="text-gray-400 hover:text-white p-1"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        onClick={() => handleEdit(channel)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(channel.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Channel Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-gray-900 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingChannel ? 'Editar Canal' : 'Novo Canal'}
              </DialogTitle>
              <DialogDescription>
                {editingChannel 
                  ? 'Atualize as informações do canal'
                  : 'Preencha os dados para criar um novo canal'
                }
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white">Nome do Canal *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="bg-white/5 border-white/20 text-white"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="category" className="text-white">Categoria</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData({...formData, category: value})}
                  >
                    <SelectTrigger className="bg-white/5 border-white/20 text-white">
                      <SelectValue placeholder="Selecione uma categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(category => (
                        <SelectItem key={category} value={category}>{category}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description" className="text-white">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-white/5 border-white/20 text-white"
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="stream_url" className="text-white">URL do Stream (m3u8) *</Label>
                <Input
                  id="stream_url"
                  type="url"
                  value={formData.stream_url}
                  onChange={(e) => setFormData({...formData, stream_url: e.target.value})}
                  className="bg-white/5 border-white/20 text-white"
                  placeholder="https://exemplo.com/stream.m3u8"
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="logo_url" className="text-white">URL do Logo</Label>
                  <Input
                    id="logo_url"
                    type="url"
                    value={formData.logo_url}
                    onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                    className="bg-white/5 border-white/20 text-white"
                    placeholder="https://exemplo.com/logo.png"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="banner_url" className="text-white">URL do Banner</Label>
                  <Input
                    id="banner_url"
                    type="url"
                    value={formData.banner_url}
                    onChange={(e) => setFormData({...formData, banner_url: e.target.value})}
                    className="bg-white/5 border-white/20 text-white"
                    placeholder="https://exemplo.com/banner.jpg"
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                />
                <Label htmlFor="is_active" className="text-white">Canal ativo</Label>
              </div>
              
              <div className="flex justify-end space-x-4 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowModal(false)}
                  className="text-white border-white/20"
                >
                  Cancelar
                </Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                  {editingChannel ? 'Atualizar' : 'Criar'} Canal
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminChannels;